import React, { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowRight,
  ArrowLeft,
  Home,
  Building2,
  Loader2,
  CheckCircle2,
  Phone,
  Zap,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIZES = [
  "Studio / 1 Bedroom",
  "2 Bedroom",
  "3 Bedroom",
  "4+ Bedroom",
  "Small Office",
  "Large Office",
  "Single Item / Heavy Lifting",
];

const InstantQuoteWizard = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [data, setData] = useState({
    move_type: "",
    size: "",
    move_from: "",
    move_to: "",
    move_date: "",
    name: "",
    phone: "",
    email: "",
  });

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const submit = async () => {
    setLoading(true);
    try {
      const payload = {
        ...data,
        email: data.email.trim() || undefined,
        move_date: data.move_date || undefined,
        packing_needs: "",
        stairs: "",
        special_items: "",
        message: "Submitted via Instant Quote wizard",
      };
      await axios.post(`${API}/quote`, payload);
      setDone(true);
      toast.success("Quote request received! We'll call you shortly.");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail?.[0]?.msg ||
          "Something went wrong. Please call (307) 265-8291."
      );
    } finally {
      setLoading(false);
    }
  };

  const canProceed1 = data.move_type && data.size;
  const canProceed2 = data.move_from.trim() && data.move_to.trim();
  const canSubmit = data.name.trim() && data.phone.trim().length >= 7;

  if (done) {
    return (
      <div
        data-testid="instant-quote-success"
        className="bg-white rounded-3xl p-8 border border-[var(--ct-border)] shadow-[0_20px_40px_rgba(0,0,0,0.06)] text-center"
      >
        <div className="w-16 h-16 rounded-full bg-[var(--ct-primary)] text-[var(--ct-secondary)] flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-8 h-8" />
        </div>
        <h3
          className="mt-5 text-2xl font-bold text-[var(--ct-primary)]"
          style={{ fontFamily: "Outfit" }}
        >
          You're in. We'll be in touch fast.
        </h3>
        <p className="mt-2 text-[var(--ct-text-muted)]">
          Need to talk now?{" "}
          <a
            className="font-semibold text-[var(--ct-primary)] underline"
            href="tel:+13072658291"
          >
            (307) 265-8291
          </a>
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="instant-quote-wizard"
      className="bg-white rounded-3xl border border-[var(--ct-border)] shadow-[0_20px_40px_rgba(0,0,0,0.06)] overflow-hidden"
    >
      {/* Header */}
      <div className="bg-[var(--ct-primary)] text-white px-7 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[var(--ct-secondary)] text-[var(--ct-primary)] flex items-center justify-center">
            <Zap className="w-5 h-5 fill-current" />
          </div>
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-[var(--ct-secondary)]">
              Instant Quote
            </div>
            <div className="font-semibold" style={{ fontFamily: "Outfit" }}>
              3 quick questions
            </div>
          </div>
        </div>
        <div className="text-xs text-white/70 font-medium">Step {step}/3</div>
      </div>

      {/* Progress */}
      <div className="h-1 bg-[var(--ct-border)]">
        <div
          className="h-full bg-[var(--ct-secondary)] transition-all duration-500"
          style={{ width: `${(step / 3) * 100}%` }}
        />
      </div>

      {/* Body */}
      <div className="p-7 md:p-8 min-h-[340px]">
        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div
              key="s1"
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -12 }}
              transition={{ duration: 0.25 }}
            >
              <h3
                className="text-2xl font-bold text-[var(--ct-primary)]"
                style={{ fontFamily: "Outfit" }}
              >
                What are you moving?
              </h3>
              <div className="mt-5 grid grid-cols-2 gap-3">
                {[
                  { k: "residential", label: "Home / Apartment", icon: Home },
                  { k: "commercial", label: "Office / Business", icon: Building2 },
                ].map((o) => {
                  const Icon = o.icon;
                  const active = data.move_type === o.k;
                  return (
                    <button
                      key={o.k}
                      type="button"
                      data-testid={`wizard-type-${o.k}`}
                      onClick={() => set("move_type", o.k)}
                      className={`p-5 rounded-2xl border-2 text-left transition-all ${
                        active
                          ? "border-[var(--ct-primary)] bg-[var(--ct-accent)]"
                          : "border-[var(--ct-border)] hover:border-[var(--ct-primary)]"
                      }`}
                    >
                      <Icon className="w-6 h-6 text-[var(--ct-primary)]" />
                      <div className="mt-3 font-semibold text-[var(--ct-primary)]">
                        {o.label}
                      </div>
                    </button>
                  );
                })}
              </div>

              <label className="block mt-6">
                <span className="text-xs font-bold uppercase tracking-[0.15em] text-[var(--ct-primary)]">
                  Size of move
                </span>
                <select
                  data-testid="wizard-size"
                  value={data.size}
                  onChange={(e) => set("size", e.target.value)}
                  className="mt-2 w-full bg-white border border-[var(--ct-border)] rounded-lg px-4 py-3 focus:ring-2 focus:ring-[var(--ct-primary)] outline-none"
                >
                  <option value="">Select size</option>
                  {SIZES.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </label>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              key="s2"
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -12 }}
              transition={{ duration: 0.25 }}
            >
              <h3
                className="text-2xl font-bold text-[var(--ct-primary)]"
                style={{ fontFamily: "Outfit" }}
              >
                Where to?
              </h3>
              <div className="mt-5 grid gap-4">
                <FieldTxt
                  label="Moving From"
                  testid="wizard-from"
                  value={data.move_from}
                  placeholder="Casper, WY"
                  onChange={(v) => set("move_from", v)}
                />
                <FieldTxt
                  label="Moving To"
                  testid="wizard-to"
                  value={data.move_to}
                  placeholder="Mills, WY"
                  onChange={(v) => set("move_to", v)}
                />
                <label className="block">
                  <span className="text-xs font-bold uppercase tracking-[0.15em] text-[var(--ct-primary)]">
                    Preferred Date (optional)
                  </span>
                  <input
                    type="date"
                    data-testid="wizard-date"
                    value={data.move_date}
                    onChange={(e) => set("move_date", e.target.value)}
                    className="mt-2 w-full bg-white border border-[var(--ct-border)] rounded-lg px-4 py-3 focus:ring-2 focus:ring-[var(--ct-primary)] outline-none"
                  />
                </label>
              </div>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div
              key="s3"
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -12 }}
              transition={{ duration: 0.25 }}
            >
              <h3
                className="text-2xl font-bold text-[var(--ct-primary)]"
                style={{ fontFamily: "Outfit" }}
              >
                Where do we call you?
              </h3>
              <p className="mt-1 text-sm text-[var(--ct-text-muted)]">
                A real Wyoming mover reaches out within the hour.
              </p>
              <div className="mt-5 grid gap-4">
                <FieldTxt
                  label="Full Name"
                  testid="wizard-name"
                  value={data.name}
                  placeholder="Jane Doe"
                  onChange={(v) => set("name", v)}
                />
                <FieldTxt
                  label="Phone"
                  testid="wizard-phone"
                  value={data.phone}
                  placeholder="(307) 555-0123"
                  onChange={(v) => set("phone", v)}
                  type="tel"
                />
                <FieldTxt
                  label="Email (optional)"
                  testid="wizard-email"
                  value={data.email}
                  placeholder="you@email.com"
                  onChange={(v) => set("email", v)}
                  type="email"
                />
              </div>
              <p className="mt-4 text-[11px] text-[var(--ct-text-muted)] leading-relaxed">
                By submitting, you agree to receive SMS updates about your quote from Crosstown Movers.
                Msg &amp; data rates may apply. Reply <strong>STOP</strong> to opt out. See{" "}
                <a href="/sms-terms" className="underline hover:text-[var(--ct-primary)]">SMS Terms</a>.
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Footer nav */}
      <div className="px-7 md:px-8 pb-7 flex items-center justify-between gap-3">
        {step > 1 ? (
          <button
            type="button"
            data-testid="wizard-back"
            onClick={() => setStep((s) => s - 1)}
            className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--ct-text-muted)] hover:text-[var(--ct-primary)]"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        ) : (
          <a
            href="tel:+13072658291"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--ct-text-muted)] hover:text-[var(--ct-primary)]"
          >
            <Phone className="w-4 h-4" /> (307) 265-8291
          </a>
        )}

        {step < 3 ? (
          <button
            type="button"
            data-testid="wizard-next"
            disabled={step === 1 ? !canProceed1 : !canProceed2}
            onClick={() => setStep((s) => s + 1)}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Continue <ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            type="button"
            data-testid="wizard-submit"
            disabled={!canSubmit || loading}
            onClick={submit}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Sending...
              </>
            ) : (
              <>
                Get My Quote <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

const FieldTxt = ({ label, testid, value, onChange, placeholder, type = "text" }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase tracking-[0.15em] text-[var(--ct-primary)]">
      {label}
    </span>
    <input
      type={type}
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="mt-2 w-full bg-white border border-[var(--ct-border)] rounded-lg px-4 py-3 focus:ring-2 focus:ring-[var(--ct-primary)] outline-none"
    />
  </label>
);

export default InstantQuoteWizard;
